import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Briefcase, ScanLine, Mic, ArrowRight, Wand2, Loader2, KanbanSquare, Zap, Sparkles } from "lucide-react";
import PageHead from "@/components/PageHead";
import JobCard from "@/components/JobCard";
import { MatchRing } from "@/components/ui-bits";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACTIONS = [
  {
    to: "/app/jobs",
    label: "Find Jobs",
    sub: "Explore global live openings matched to your Profile",
    icon: Briefcase,
    color: "bg-electric/10 text-electric",
  },
  {
    to: "/app/ats",
    label: "Run ATS Match",
    sub: "Scan and benchmark your CV against target job descriptions",
    icon: ScanLine,
    color: "bg-coral/10 text-coral",
  },
  {
    to: "/app/customize",
    label: "Tailor CV",
    sub: "Generate an ATS-proof customized resume for a specific job",
    icon: Wand2,
    color: "bg-aqua/10 text-aqua",
  },
  {
    to: "/app/tracker",
    label: "Track Applications",
    sub: "Manage interviews, offers and follow-ups in a visual Kanban",
    icon: KanbanSquare,
    color: "bg-violet/10 text-violet",
  },
];

export default function Dashboard() {
  const { user, refreshBalance } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [topJobs, setTopJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [saved, setSaved] = useState({});

  const p = user?.profile || {};
  const skills = p.skills || [];
  const strength = (() => {
    let score = 0;
    if (p.currentRole || p.current_role) score += 20;
    if (p.targetRole || p.target_role) score += 15;
    if (skills.length > 0) score += Math.min(25, skills.length * 4);
    if (p.bio && p.bio.length > 20) score += 15;
    if ((p.experiences || []).length > 0) score += 15;
    if ((p.education || []).length > 0) score += 10;
    return Math.min(100, Math.max(0, score));
  })();

  useEffect(() => {
    let active = true;
    const fetchTopMatches = async () => {
      try {
        const { data } = await api.get("/jobs", {
          params: { q: "", country: "all" },
        });
        if (active && data?.jobs) {
          setTopJobs(data.jobs.slice(0, 3));
        }
      } catch (_) {
        if (active) setTopJobs([]);
      } finally {
        if (active) setLoadingJobs(false);
      }
    };

    const fetchSaved = async () => {
      try {
        const { data } = await api.get("/applications");
        const map = {};
        (data?.applications || []).forEach((app) => {
          if (app.id) map[app.id] = app.id;
          const k = `${app.company}|${app.role}`.toLowerCase();
          map[k] = app.id;
        });
        if (active) setSaved(map);
      } catch (_) {}
    };

    fetchTopMatches();
    fetchSaved();
    return () => { active = false; };
  }, [user]);

  const handleToggleSave = async (job) => {
    const jobKey = `${job.company}|${job.role}`.toLowerCase();
    const existingAppId = saved[job.id] || saved[jobKey];

    if (existingAppId) {
      try {
        await api.delete(`/applications/${existingAppId}`);
      } catch (_) {}
      setSaved((prev) => {
        const next = { ...prev };
        delete next[job.id];
        delete next[jobKey];
        return next;
      });
      toast(`Removed ${job.role} from Application Tracker`);
    } else {
      try {
        const { data } = await api.post("/applications", {
          id: job.id,
          company: job.company,
          role: job.role,
          status: "Saved",
          location: job.location,
          match_score: job.match,
        });
        const createdApp = data?.application || { id: job.id, company: job.company, role: job.role };
        setSaved((prev) => ({ ...prev, [job.id]: createdApp.id, [jobKey]: createdApp.id }));
        toast.success(`Saved ${job.role} to Application Tracker`);
      } catch (_) {
        setSaved((prev) => ({ ...prev, [job.id]: job.id, [jobKey]: job.id }));
        toast.success(`Saved ${job.role} to Application Tracker`);
      }
    }
  };

  const handleApply = async (job) => {
    try {
      await api.post("/applications", {
        company: job.company,
        role: job.role,
        status: "Clicked",
        location: job.location,
        match_score: job.match,
      });
    } catch (_) {}

    const targetUrl = (job.apply_url && job.apply_url !== "#") ? job.apply_url : (job.link || job.url);
    if (targetUrl && targetUrl !== "#") {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
      toast.success(`Opening job posting for ${job.role}`);
    } else {
      toast.success(`Applied to ${job.role} at ${job.company} (Saved to Tracker)`);
    }
  };

  const displayName = () => {
    if (!user?.name) return "there";
    if (user.name.includes("@")) {
      const prefix = user.name.split("@")[0];
      return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    }
    return user.name.split(" ")[0];
  };

  return (
    <div>
      <PageHead
        label={`Welcome back, ${displayName()}`}
        title="Your career at a glance"
        sub="Everything from CV to interview, in one place."
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="card-surface p-6 lg:col-span-2">
          <h2 className="font-heading text-lg font-bold">Quick actions</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <Link
                  key={a.to}
                  to={a.to}
                  className="group flex flex-col justify-between rounded-2xl border border-border bg-card p-4 transition-all hover:border-electric/40 hover:shadow-card"
                >
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${a.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="mt-4">
                    <h3 className="font-heading text-base font-bold leading-tight">{a.label}</h3>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{a.sub}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="card-surface flex flex-col items-center justify-center p-6 text-center">
          <MatchRing value={strength} size={120} stroke={10} color="hsl(var(--chart-4))" />
          <p className="mt-4 font-heading text-lg font-bold">Profile strength</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {strength >= 80 ? "Profile well-optimized for matching" : "Complete Profile to reach 100%"}
          </p>
          <Link
            to="/app/passport"
            className="cv-btn-ghost mt-4 border border-border text-xs w-full max-w-[200px] justify-center"
          >
            Open Profile <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold">Top job matches</h2>
        <Link to="/app/jobs" className="text-sm font-semibold text-electric">View all</Link>
      </div>

      {loadingJobs ? (
        <div className="mt-4 flex min-h-[160px] items-center justify-center rounded-2xl border border-dashed border-border">
          <Loader2 className="h-6 w-6 animate-spin text-electric" />
          <span className="ml-2 text-xs text-muted-foreground">Loading top live matches…</span>
        </div>
      ) : topJobs.length > 0 ? (
        <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {topJobs.map((j) => {
            const isBookmarked = !!(saved[j.id] || saved[`${j.company}|${j.role}`.toLowerCase()]);
            return (
              <JobCard
                key={j.id}
                job={j}
                saved={isBookmarked}
                onApply={() => handleApply(j)}
                onSave={() => handleToggleSave(j)}
              />
            );
          })}
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
          No live jobs found right now. Explore all roles in <Link to="/app/jobs" className="text-electric underline font-semibold">Find Jobs</Link>.
        </div>
      )}
    </div>
  );
}
